/**
 * Валидаторы для различных реквизитов
 * ИНН, ОГРН, БИК и другие проверки для корректного ввода данных
 */

// ============================================================================
// ВАЛИДАЦИЯ ИНН
// ============================================================================

/**
 * Проверяет валидность ИНН
 * @param {string} inn - ИНН для проверки
 * @returns {{isValid: boolean, errorMessage: string|null, entityType: string|null}}
 */
export function validateINN(inn) {
  // Удаляем пробелы
  const cleanINN = inn.trim();

  // Проверка на пустоту
  if (!cleanINN) {
    return {
      isValid: false,
      errorMessage: "ИНН не может быть пустым",
      entityType: null
    };
  }

  // Проверка что все символы - цифры
  if (!/^\d+$/.test(cleanINN)) {
    return {
      isValid: false,
      errorMessage: "ИНН должен содержать только цифры",
      entityType: null
    };
  }

  // Проверка длины
  const length = cleanINN.length;
  if (length !== 10 && length !== 12) {
    return {
      isValid: false,
      errorMessage: "ИНН должен содержать 10 цифр (ЮЛ) или 12 цифр (ИП/ФЛ)",
      entityType: null
    };
  }

  // Проверка контрольной суммы
  if (length === 10) {
    return validateINN10(cleanINN);
  } else {
    return validateINN12(cleanINN);
  }
}

/**
 * Проверка ИНН юридического лица (10 цифр)
 * @private
 */
function validateINN10(inn) {
  const coefficients = [2, 4, 10, 3, 5, 9, 4, 6, 8];
  
  const checksum = calculateChecksum(inn, coefficients, 9);
  if (checksum === null) {
    return {
      isValid: false,
      errorMessage: "Ошибка вычисления контрольной суммы",
      entityType: "juridical"
    };
  }

  const lastDigit = parseInt(inn[9], 10);
  
  if (checksum === lastDigit) {
    return {
      isValid: true,
      errorMessage: null,
      entityType: "juridical"
    };
  } else {
    return {
      isValid: false,
      errorMessage: "ИНН имеет неверную контрольную сумму",
      entityType: "juridical"
    };
  }
}

/**
 * Проверка ИНН физического лица / ИП (12 цифр)
 * @private
 */
function validateINN12(inn) {
  // Проверка 11-й цифры
  const coefficients11 = [7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
  const checksum11 = calculateChecksum(inn, coefficients11, 10);
  
  // Проверка 12-й цифры
  const coefficients12 = [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
  const checksum12 = calculateChecksum(inn, coefficients12, 11);
  
  if (checksum11 === null || checksum12 === null) {
    return {
      isValid: false,
      errorMessage: "Ошибка вычисления контрольной суммы",
      entityType: "individual"
    };
  }

  const digit11 = parseInt(inn[10], 10);
  const digit12 = parseInt(inn[11], 10);
  
  if (checksum11 === digit11 && checksum12 === digit12) {
    return {
      isValid: true,
      errorMessage: null,
      entityType: "individual"
    };
  } else {
    return {
      isValid: false,
      errorMessage: "ИНН имеет неверную контрольную сумму",
      entityType: "individual"
    };
  }
}

/**
 * Вычисляет контрольную сумму
 * @private
 */
function calculateChecksum(inn, coefficients, position) {
  const digits = inn.substring(0, position).split('').map(d => parseInt(d, 10));
  
  if (digits.length !== coefficients.length) {
    return null;
  }
  
  const sum = digits.reduce((acc, digit, index) => {
    return acc + (digit * coefficients[index]);
  }, 0);
  
  return (sum % 11) % 10;
}

/**
 * Определяет тип субъекта по ИНН
 * @param {string} inn - ИНН
 * @returns {string|null} - "juridical" или "individual"
 */
export function getEntityType(inn) {
  const cleanINN = inn.trim();
  
  switch (cleanINN.length) {
    case 10:
      return "juridical"; // Юридическое лицо
    case 12:
      return "physical"; // Физическое лицо (12 цифр ИНН)
    default:
      return null;
  }
}

/**
 * Получить отображаемое имя типа субъекта
 * @param {string} entityType - Тип субъекта ("juridical" или "individual")
 * @returns {string}
 */
export function getEntityTypeDisplayName(entityType) {
  switch (entityType) {
    case "juridical":
      return "Юридическое лицо";
    case "individual":
      return "ИП / Физическое лицо";
    default:
      return "";
  }
}

// ============================================================================
// ВАЛИДАЦИЯ ОГРН/ОГРНИП
// ============================================================================

/**
 * Проверяет валидность ОГРН/ОГРНИП
 * @param {string} ogrn - ОГРН для проверки
 * @returns {{isValid: boolean, errorMessage: string|null}}
 */
export function validateOGRN(ogrn) {
  const cleanOGRN = ogrn.trim();

  // Проверка на пустоту
  if (!cleanOGRN) {
    return {
      isValid: false,
      errorMessage: "ОГРН не может быть пустым"
    };
  }

  // Проверка что все символы - цифры
  if (!/^\d+$/.test(cleanOGRN)) {
    return {
      isValid: false,
      errorMessage: "ОГРН должен содержать только цифры"
    };
  }

  // Проверка длины
  const length = cleanOGRN.length;
  if (length !== 13 && length !== 15) {
    return {
      isValid: false,
      errorMessage: "ОГРН должен содержать 13 цифр (ЮЛ) или 15 цифр (ИП)"
    };
  }

  // Проверка контрольной суммы
  if (length === 13) {
    return validateOGRN13(cleanOGRN);
  } else {
    return validateOGRN15(cleanOGRN);
  }
}

/**
 * Проверка ОГРН юридического лица (13 цифр)
 * @private
 */
function validateOGRN13(ogrn) {
  const first12Digits = ogrn.substring(0, 12);
  const checksum = parseInt(ogrn[12], 10);
  
  const remainder = parseInt(first12Digits) % 11;
  const expectedChecksum = remainder < 10 ? remainder : 0;
  
  if (checksum === expectedChecksum) {
    return {
      isValid: true,
      errorMessage: null
    };
  } else {
    return {
      isValid: false,
      errorMessage: "ОГРН имеет неверную контрольную сумму"
    };
  }
}

/**
 * Проверка ОГРНИП (15 цифр)
 * @private
 */
function validateOGRN15(ogrn) {
  const first14Digits = ogrn.substring(0, 14);
  const checksum = parseInt(ogrn[14], 10);
  // ФНС: младший разряд остатка от деления числа из первых 14 цифр на 13 (остаток 0–12).
  const remainder = parseInt(first14Digits, 10) % 13;
  const expectedChecksum = remainder % 10;
  
  if (checksum === expectedChecksum) {
    return {
      isValid: true,
      errorMessage: null
    };
  } else {
    return {
      isValid: false,
      errorMessage: "ОГРНИП имеет неверную контрольную сумму"
    };
  }
}

// ============================================================================
// ВАЛИДАЦИЯ БИК
// ============================================================================

/**
 * Проверяет валидность БИК (Банковский идентификационный код)
 * @param {string} bik - БИК для проверки
 * @returns {{isValid: boolean, errorMessage: string|null}}
 */
export function validateBIK(bik) {
  const cleanBIK = bik.trim();

  // Проверка на пустоту
  if (!cleanBIK) {
    return {
      isValid: false,
      errorMessage: "БИК не может быть пустым"
    };
  }

  // Проверка что все символы - цифры
  if (!/^\d+$/.test(cleanBIK)) {
    return {
      isValid: false,
      errorMessage: "БИК должен содержать только цифры"
    };
  }

  // БИК всегда состоит из 9 цифр
  if (cleanBIK.length !== 9) {
    return {
      isValid: false,
      errorMessage: "БИК должен содержать ровно 9 цифр"
    };
  }

  return {
    isValid: true,
    errorMessage: null
  };
}

// ============================================================================
// ВАЛИДАЦИЯ РАСЧЕТНОГО СЧЕТА
// ============================================================================

/**
 * Проверяет валидность расчетного счета
 * @param {string} account - Расчетный счет для проверки
 * @returns {{isValid: boolean, errorMessage: string|null}}
 */
export function validateAccount(account) {
  const cleanAccount = account.trim();

  // Проверка на пустоту
  if (!cleanAccount) {
    return {
      isValid: false,
      errorMessage: "Расчетный счет не может быть пустым"
    };
  }

  // Проверка что все символы - цифры
  if (!/^\d+$/.test(cleanAccount)) {
    return {
      isValid: false,
      errorMessage: "Расчетный счет должен содержать только цифры"
    };
  }

  // Расчетный счет всегда состоит из 20 цифр
  if (cleanAccount.length !== 20) {
    return {
      isValid: false,
      errorMessage: "Расчетный счет должен содержать ровно 20 цифр"
    };
  }

  return {
    isValid: true,
    errorMessage: null
  };
}

// ============================================================================
// ВАЛИДАЦИЯ ТЕЛЕФОНА
// ============================================================================

/**
 * Проверяет валидность российского телефона
 * @param {string} phone - Телефон для проверки
 * @returns {{isValid: boolean, errorMessage: string|null}}
 */
export function validateRussianPhone(phone) {
  const cleanPhone = phone.trim();

  // Проверка на пустоту (поле необязательное)
  if (!cleanPhone) {
    return {
      isValid: true,
      errorMessage: null
    };
  }

  // Удаляем все нецифровые символы кроме + в начале
  const digitsOnly = cleanPhone.replace(/[^\d+]/g, '');
  
  // Проверка формата: +7XXXXXXXXXX или 8XXXXXXXXXX
  if (!/^(\+7|8)\d{10}$/.test(digitsOnly)) {
    return {
      isValid: false,
      errorMessage: "Телефон должен быть в формате +7XXXXXXXXXX или 8XXXXXXXXXX"
    };
  }

  return {
    isValid: true,
    errorMessage: null
  };
}

// ============================================================================
// ВАЛИДАЦИЯ TELEGRAM USERNAME
// ============================================================================

/**
 * Проверяет валидность Telegram username
 * @param {string} telegram - Telegram username для проверки
 * @returns {{isValid: boolean, errorMessage: string|null}}
 */
export function validateTelegram(telegram) {
  const cleanTelegram = telegram.trim();

  // Проверка на пустоту (поле необязательное)
  if (!cleanTelegram) {
    return {
      isValid: true,
      errorMessage: null
    };
  }

  // Telegram username должен начинаться с @ и содержать 5-32 символа (буквы, цифры, подчеркивания)
  if (!/^@[a-zA-Z0-9_]{5,32}$/.test(cleanTelegram)) {
    return {
      isValid: false,
      errorMessage: "Telegram username должен начинаться с @ и содержать 5-32 символа (буквы, цифры, _)"
    };
  }

  return {
    isValid: true,
    errorMessage: null
  };
}

// ============================================================================
// ОБЩАЯ ВАЛИДАЦИЯ ФОРМЫ КОМПАНИИ
// ============================================================================

/**
 * Валидирует все поля формы информации о компании
 * @param {object} companyInfo - Данные компании
 * @returns {{isValid: boolean, errors: object}}
 */
export function validateCompanyInfo(companyInfo) {
  const errors = {};

  // Валидация ИНН
  if (!companyInfo.inn || !companyInfo.inn.trim()) {
    errors.inn = "ИНН обязателен для заполнения";
  } else {
    const innValidation = validateINN(companyInfo.inn);
    if (!innValidation.isValid) {
      errors.inn = innValidation.errorMessage;
    }
  }

  // Валидация ОГРН
  if (!companyInfo.ogrn || !companyInfo.ogrn.trim()) {
    errors.ogrn = "ОГРН обязателен для заполнения";
  } else {
    const ogrnValidation = validateOGRN(companyInfo.ogrn);
    if (!ogrnValidation.isValid) {
      errors.ogrn = ogrnValidation.errorMessage;
    }
  }

  // Валидация ФИО/названия
  if (!companyInfo.name || !companyInfo.name.trim()) {
    errors.name = "ФИО или название обязательны для заполнения";
  }

  // Валидация адреса
  if (!companyInfo.address || !companyInfo.address.trim()) {
    errors.address = "Адрес обязателен для заполнения";
  }

  // Валидация телефона (необязательное поле)
  if (companyInfo.phone && companyInfo.phone.trim()) {
    const phoneValidation = validateRussianPhone(companyInfo.phone);
    if (!phoneValidation.isValid) {
      errors.phone = phoneValidation.errorMessage;
    }
  }

  // Валидация Telegram (необязательное поле)
  if (companyInfo.telegram && companyInfo.telegram.trim()) {
    const telegramValidation = validateTelegram(companyInfo.telegram);
    if (!telegramValidation.isValid) {
      errors.telegram = telegramValidation.errorMessage;
    }
  }

  // Валидация БИК (необязательное поле)
  if (companyInfo.bank_bik && companyInfo.bank_bik.trim()) {
    const bikValidation = validateBIK(companyInfo.bank_bik);
    if (!bikValidation.isValid) {
      errors.bank_bik = bikValidation.errorMessage;
    }
  }

  // Валидация расчетного счета (необязательное поле)
  if (companyInfo.bank_account && companyInfo.bank_account.trim()) {
    const accountValidation = validateAccount(companyInfo.bank_account);
    if (!accountValidation.isValid) {
      errors.bank_account = accountValidation.errorMessage;
    }
  }

  // Валидация корреспондентского счета (необязательное поле)
  if (companyInfo.correspondent_account && companyInfo.correspondent_account.trim()) {
    const correspondentValidation = validateAccount(companyInfo.correspondent_account);
    if (!correspondentValidation.isValid) {
      errors.correspondent_account = correspondentValidation.errorMessage;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors: errors
  };
}

